import { useState } from "react";
import { ImageIcon, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

interface ScreenshotPlaceholderProps {
  caption: string;
  className?: string;
  /** real product screenshot; when absent a dashed placeholder is shown */
  src?: string;
}

/**
 * Captioned 16:10 product-screenshot frame. When `src` is provided the thumbnail
 * is clickable and opens a large lightbox dialog; otherwise a dashed placeholder
 * is shown. The caption lives beneath the frame either way.
 */
export function ScreenshotPlaceholder({
  caption,
  className,
  src,
}: ScreenshotPlaceholderProps) {
  const [open, setOpen] = useState(false);

  return (
    <figure className={cn("flex flex-col gap-2", className)}>
      {src ? (
        <>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={`Enlarge screenshot: ${caption}`}
            className="group relative block aspect-[16/10] w-full cursor-zoom-in overflow-hidden rounded-lg border border-border shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <img
              src={src}
              alt={caption}
              loading="lazy"
              className="size-full object-cover object-top"
            />
            {/* hover affordance */}
            <span className="pointer-events-none absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/15" />
            <span className="pointer-events-none absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-black/65 px-2 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
              <Maximize2 className="size-3" />
              Click to enlarge
            </span>
          </button>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="flex w-[min(1400px,95vw)] max-w-[95vw] flex-col items-center gap-2 border-0 bg-transparent p-0 shadow-none sm:max-w-[95vw]">
              <DialogTitle className="sr-only">{caption}</DialogTitle>
              <img
                src={src}
                alt={caption}
                className="h-auto max-h-[88vh] w-full rounded-lg border border-border bg-background object-contain shadow-2xl"
              />
              <figcaption className="text-center text-xs text-white/90 drop-shadow">
                {caption}
              </figcaption>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div className="aspect-[16/10] w-full rounded-lg border-2 border-dashed border-border bg-muted/50 flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <ImageIcon className="size-7 opacity-60" />
          <span className="text-[11px] font-medium uppercase tracking-wide opacity-70">
            Screenshot
          </span>
        </div>
      )}
      <figcaption className="text-xs text-muted-foreground leading-relaxed">
        {caption}
      </figcaption>
    </figure>
  );
}
